import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@components/ui/dialog';
import { cn } from '@components/utils/classname';

import { computeKeepAliveInclude, validateFlowStepConfig } from '../flow-core';
import { DialogFlowContext } from './DialogFlowContext';
import type {
  DialogFlowInstance,
  DialogFlowProps,
  DialogFlowStepComponent,
  DialogFlowStepProps,
  DialogFlowStepStatus,
} from './types';

const DialogFlowComponent = forwardRef<
  DialogFlowInstance<any>,
  DialogFlowProps
>((props, ref) => {
  const {
    open,
    steps,
    defaultStatus,
    dialogProps = {},
    cacheStateOnClose = false,
    historyMode = false,
    keepAlive = false,
    onProceed,
    onDrawback,
    onChange,
    onClose,
    onOpenChange,
    children,
    dialogContentProps = {},
  } = props;

  // Validate configuration
  useEffect(() => {
    validateFlowStepConfig(steps);
  }, [steps]);

  const [dialogState, setDialogState] = useState(open);
  const [stepHistoryStack, setStepHistoryStack] = useState<
    DialogFlowStepComponent[]
  >([]);
  const defaultStatusValue = useMemo(() => {
    return (defaultStatus ?? steps[0]?.status) as DialogFlowStepStatus;
  }, [defaultStatus, steps]);
  const [status, setStatus] =
    useState<DialogFlowStepStatus>(defaultStatusValue);
  const [stepValues, setStepValues] = useState<
    Record<DialogFlowStepStatus, any[]>
  >({});
  const [prevPassedValue, setPrevPassedValue] = useState<any[]>([]);
  const [internalDialogProps, setInternalDialogProps] = useState(steps);
  const [isPreventDefaultFlow, setIsPreventDefaultFlow] = useState(false);

  useEffect(() => {
    setInternalDialogProps(steps);
  }, [steps]);

  const currentStep = useMemo<DialogFlowStepComponent | undefined>(() => {
    return internalDialogProps.find((item) => status === item.status);
  }, [internalDialogProps, status]);

  // Compute dialog props for the current step
  const currentDialogProps = useMemo(() => {
    return { ...dialogProps, ...currentStep?.props };
  }, [dialogProps, currentStep]);

  // Compute keepAlive configuration
  const keepAliveInclude = useMemo(() => {
    return computeKeepAliveInclude(internalDialogProps, keepAlive);
  }, [internalDialogProps, keepAlive]);

  const handleSetDialogState = useCallback(
    (state: boolean) => {
      setDialogState(state);
      onOpenChange?.(state);
    },
    [onOpenChange],
  );

  // Sync external open state
  useEffect(() => {
    handleSetDialogState(open);
  }, [handleSetDialogState, open]);

  const handleClose = useCallback(() => {
    handleSetDialogState(false);
    setStepHistoryStack([]);
    if (!cacheStateOnClose) {
      setStatus(defaultStatusValue);
    }
    onClose?.();
  }, [cacheStateOnClose, defaultStatusValue, onClose, handleSetDialogState]);

  const handleUpdateStepDialogProps = useCallback(
    (
      targetStatus: DialogFlowStepStatus,
      newDialogProps: DialogFlowStepProps,
    ) => {
      setInternalDialogProps(
        (prevSteps) =>
          prevSteps.map((item) =>
            targetStatus === item.status
              ? { ...item, props: newDialogProps }
              : item,
          ) as DialogFlowStepComponent[],
      );
    },
    [],
  );

  const handleChangeStatus = useCallback(
    (nextStatus: DialogFlowStepStatus, ...args: any[]) => {
      const curStep = internalDialogProps.find(
        (item) => item.status === status,
      );
      const nextStep = internalDialogProps.find(
        (item) => nextStatus === item.status,
      );

      if (!nextStep) {
        throw new Error('The status you set is invalid.');
      }

      setIsPreventDefaultFlow(true);
      onChange?.(status, nextStatus, currentDialogProps, ...args);
      setStatus(nextStatus);

      setPrevPassedValue(args);
      setStepValues((prev) => ({ ...prev, [status]: args }));

      if (curStep) {
        setStepHistoryStack((prev) => [...prev, curStep]);
      }

      setIsPreventDefaultFlow(false);
    },
    [internalDialogProps, status, currentDialogProps, onChange],
  );

  const handleChangeCallback = useCallback(
    (
      currentStatus: DialogFlowStepStatus,
      nextStatus: DialogFlowStepStatus,
      itemDialogProps: DialogFlowStepProps,
      ...args: any[]
    ) => {
      onChange?.(currentStatus, nextStatus, itemDialogProps, ...args);

      if (!isPreventDefaultFlow) {
        setStatus(nextStatus);
        setPrevPassedValue(args);
        setStepValues((prev) => ({ ...prev, [currentStatus]: args }));
      }

      setIsPreventDefaultFlow(false);
    },
    [onChange, isPreventDefaultFlow],
  );

  const handleProceed = useCallback(
    (...args: any[]) => {
      const curIdx = internalDialogProps.findIndex(
        (item) => item.status === status,
      );
      const { status: curStatus, props: itemProps = {} } = internalDialogProps[
        curIdx
      ] as DialogFlowStepComponent;

      if (curIdx === internalDialogProps.length - 1) {
        setStepValues((prev) => ({ ...prev, [curStatus]: args }));
        return;
      }

      const nextStatus = internalDialogProps[curIdx + 1]
        ?.status as DialogFlowStepStatus;

      onProceed?.(curStatus, nextStatus, itemProps, ...args);
      handleChangeCallback(curStatus, nextStatus, itemProps, ...args);
      setStepHistoryStack((prev) => [
        ...prev,
        internalDialogProps[curIdx] as DialogFlowStepComponent,
      ]);
    },
    [internalDialogProps, status, onProceed, handleChangeCallback],
  );

  const handleDrawback = useCallback(
    (...args: any[]) => {
      const curIdx = internalDialogProps.findIndex(
        (item) => item.status === status,
      );
      const { status: curStatus, props: itemProps = {} } = internalDialogProps[
        curIdx
      ] as DialogFlowStepComponent;

      if (historyMode && !stepHistoryStack.length && curIdx === 0) return;
      if (!historyMode && curIdx === 0) return;

      const leftStatus = internalDialogProps[curIdx - 1]
        ?.status as DialogFlowStepStatus;
      const toStatus =
        historyMode && stepHistoryStack.length
          ? (stepHistoryStack[stepHistoryStack.length - 1]
              ?.status as DialogFlowStepStatus)
          : leftStatus;

      onDrawback?.(curStatus, toStatus, itemProps, ...args);
      handleChangeCallback(curStatus, toStatus, itemProps, ...args);

      setStepHistoryStack((prev) => {
        const newStack = [...prev];
        newStack.pop();
        return newStack;
      });
    },
    [
      internalDialogProps,
      status,
      historyMode,
      stepHistoryStack,
      onDrawback,
      handleChangeCallback,
    ],
  );

  // Expose methods to parent component via ref
  useImperativeHandle(
    ref,
    () => ({
      updateDialogProps: handleUpdateStepDialogProps,
      setStatus: handleChangeStatus,
      setDialogState: handleSetDialogState,
      closeDialog: handleClose,
      proceed: handleProceed,
      drawback: handleDrawback,
      getStepsValue: () => stepValues,
      getStepValue: (key: DialogFlowStepStatus) => stepValues[key] ?? [],
      getStatus: () => status,
    }),
    [
      handleUpdateStepDialogProps,
      handleChangeStatus,
      handleSetDialogState,
      handleClose,
      handleProceed,
      handleDrawback,
      stepValues,
      status,
    ],
  );

  // Context value
  const contextValue = useMemo(
    () => ({
      prevValue: prevPassedValue,
      stepValues,
      proceed: handleProceed,
      drawback: handleDrawback,
      closeDialog: handleClose,
      setStatus: handleChangeStatus,
      updateDialogProps: (props: DialogFlowStepProps) =>
        handleUpdateStepDialogProps(status, props),
    }),
    [
      prevPassedValue,
      stepValues,
      handleProceed,
      handleDrawback,
      handleClose,
      handleChangeStatus,
      handleUpdateStepDialogProps,
      status,
    ],
  );

  // Render current step component
  const renderCurrentStep = useCallback(() => {
    // If custom children render is provided
    if (children) {
      return children(status);
    }

    // If keepAlive is enabled, render all steps that need to be cached
    if (keepAliveInclude.length > 0) {
      return (
        <>
          {internalDialogProps.map((step) => {
            const isActive = step.status === status;
            const shouldCache =
              step.status && keepAliveInclude.includes(step.status);

            // Only render active step or steps that need to be cached
            if (!isActive && !shouldCache) {
              return null;
            }

            const StepComponent = step.component;
            const stepProps = step.componentProps?.() ?? {};

            const componentElement = (
              <StepComponent
                key={String(step.status)}
                onProceed={handleProceed}
                onDrawback={handleDrawback}
                onDialogClose={handleClose}
                onSetStatus={handleChangeStatus}
                onUpdateDialogProps={(props: DialogFlowStepProps) =>
                  handleUpdateStepDialogProps(step.status, props)
                }
                {...stepProps}
              />
            );

            // Use display:none to hide inactive steps while keeping them mounted
            if (shouldCache) {
              return (
                <div
                  key={step.status}
                  style={{
                    display: isActive ? 'block' : 'none',
                  }}
                >
                  {componentElement}
                </div>
              );
            }

            return componentElement;
          })}
        </>
      );
    }

    // When keepAlive is not enabled, only render the current step
    const currentStep = internalDialogProps.find(
      (item) => item.status === status,
    );
    if (!currentStep) return null;

    const StepComponent = currentStep.component;
    const stepProps = currentStep.componentProps?.() ?? {};

    return (
      <StepComponent
        key={String(status)}
        onProceed={handleProceed}
        onDrawback={handleDrawback}
        onDialogClose={handleClose}
        onSetStatus={handleChangeStatus}
        onUpdateDialogProps={(props: DialogFlowStepProps) =>
          handleUpdateStepDialogProps(status, props)
        }
        {...stepProps}
      />
    );
  }, [
    children,
    status,
    internalDialogProps,
    handleProceed,
    handleDrawback,
    handleClose,
    handleChangeStatus,
    handleUpdateStepDialogProps,
    keepAliveInclude,
  ]);

  const stepContent = renderCurrentStep();

  const getStepText = useCallback(
    (text: string | ReactNode | ((prevValue: any[]) => string | ReactNode)) => {
      if (typeof text === 'function') {
        return text(prevPassedValue);
      }
      return text;
    },
    [prevPassedValue],
  );

  const mergedDialogContentProps = useMemo(() => {
    return {
      ...dialogContentProps,
      ...currentDialogProps.dialogContentProps,
      className: cn(
        'w-full max-w-full sm:w-auto sm:max-w-[1200px]',
        dialogContentProps?.className,
        currentDialogProps.dialogContentProps?.className,
      ),
    };
  }, [dialogContentProps, currentDialogProps]);

  return (
    <DialogFlowContext.Provider value={contextValue}>
      <Dialog
        open={dialogState}
        {...currentDialogProps}
        onOpenChange={(open) => {
          if (open) {
            handleSetDialogState(true);
          } else {
            handleClose();
          }
        }}
      >
        <DialogContent {...mergedDialogContentProps}>
          {currentStep?.title && (
            <DialogHeader>
              <DialogTitle>{getStepText(currentStep.title)}</DialogTitle>
              {currentStep?.description && (
                <DialogDescription>
                  {getStepText(currentStep.description)}
                </DialogDescription>
              )}
            </DialogHeader>
          )}
          {stepContent}
        </DialogContent>
      </Dialog>
    </DialogFlowContext.Provider>
  );
});

DialogFlowComponent.displayName = 'DialogFlow';

export default DialogFlowComponent;
